import { Graphics } from 'pixi.js';
import { Boss } from './boss-base';
import { getTexture } from '@/assets/resolver';
import { COLORS } from '@/config/colors';
import {
  CORE_HP_PCT, DRONES_AT, ORBIT, RAGE_AT, RING, STAGE_INDEX, STAGE_PATTERNS,
  TURRET_FIRE, TURRET_HP_SHARE, TURRET_ORBIT, TURRET_RADIUS, TURRET_SHOT_SPEED, TURRET_SPIN,
  WARDEN, WINDOWS, buildTurrets, nearestTurret, type Stage, type Turret,
} from './warden-parts';
import { WardenLaser } from './warden-laser';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';

export { WARDEN } from './warden-parts';

/**
 * B5 — The Warden. Finale boss, 3 stages (docs/02):
 * ARMOR — body sealed (clink), 4 orbiting shoulder turrets are the real HP;
 *         lockdown walls with a roaming hole
 * CORE  — hatch blown open; aimed fans + radials + rotating laser sweep.
 *         The hatch SEALS while the laser charges/fires, then VENTS (bonus)
 * RAGE  — locks to arena center, dense twin spiral + shockwaves while a red
 *         containment ring shrinks; the core only opens in short BREATHS
 */
export class WardenBoss extends Boss {
  private stage: Stage = 'armor';
  private turrets: Turret[] = [];
  private dronesCalled = false;
  private readonly laser = new WardenLaser();
  /** Core: bonus window after each sweep; rage: breath cycle clock */
  private vent = 0;
  private breath: number = WINDOWS.breathEvery;
  // Rage containment ring
  private ringRadius = RING.start;
  private readonly beamG = new Graphics();
  private readonly ringG = new Graphics();
  private time = 0;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(WARDEN, level, x, y, homeX, homeY);
    this.view.addChild(this.beamG, this.ringG);
    this.turrets = buildTurrets(this.view, x, y);
    this.splitHp();
    this.enterStage('armor');
  }

  /** Turret share of the SCALED bar — no jump at the last turret (X1). */
  private splitHp(): void {
    const turretHp = Math.round(this.maxHp * TURRET_HP_SHARE);
    for (const t of this.turrets) t.hp = turretHp;
  }

  /** Power scaling hook: re-split the turret share of the scaled bar. */
  protected override scaleParts(_mul: number): void {
    this.splitHp();
  }

  private enterStage(stage: Stage): void {
    this.stage = stage;
    this.phaseIndex = STAGE_INDEX[stage]; // evolve layers key off the stage
    // Signature + tier extras + evolve, all on the tier ramp (A2/A3)
    this.runners = this.makeRunners(STAGE_PATTERNS[stage], true);
    if (stage === 'core') {
      this.body.texture = getTexture('boss/warden/body', 1);
    } else if (stage === 'rage') {
      this.ringRadius = RING.start;
    }
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    if (this.dead) return;
    this.time += dt;
    this.hitFlash -= dt;
    this.prevX = this.x;
    this.prevY = this.y;
    this.updateReinforce(dt);

    // Movement: slow orbit until rage, then lock to home center
    if (this.stage === 'rage') {
      this.x += (this.homeX - this.x) * Math.min(1, ORBIT.lock * dt);
      this.y += (this.homeY - this.y) * Math.min(1, ORBIT.lock * dt);
    } else {
      this.moveAngle += ORBIT.rate * this.levelSpeedMul * dt;
      const tx = this.homeX + Math.cos(this.moveAngle) * ORBIT.rx;
      const ty = this.homeY + Math.sin(this.moveAngle * ORBIT.yFreq) * ORBIT.ry;
      this.x += (tx - this.x) * Math.min(1, ORBIT.follow * dt);
      this.y += (ty - this.y) * Math.min(1, ORBIT.follow * dt);
    }

    this.fireRunners(dt, px, py, bullets, telegraphs);

    if (this.stage === 'armor') this.updateTurrets(dt, px, py, bullets);

    // Core/rage extras
    if (this.stage !== 'armor') {
      if (this.laser.update(dt, this.x, this.y, px, py, this.intensity)) this.vent = WINDOWS.ventTime;
      this.vent -= dt;
      if (this.stage === 'rage') {
        this.breath -= dt;
        if (this.breath <= -WINDOWS.breathOpen) this.breath = WINDOWS.breathEvery;
      }
      if (!this.dronesCalled && this.hpPct <= DRONES_AT) {
        this.dronesCalled = true;
        this.spawnMinions?.('drone', 2);
      }
      if (this.stage === 'core' && this.hpPct <= RAGE_AT) this.enterStage('rage');
    }
    if (this.stage === 'rage' && this.ringRadius > RING.end) {
      this.ringRadius = Math.max(
        RING.end,
        this.ringRadius - ((RING.start - RING.end) / RING.shrinkTime) * dt,
      );
    }
  }

  private updateTurrets(dt: number, px: number, py: number, bullets: BulletSystem): void {
    for (const t of this.turrets) {
      if (!t.alive) continue;
      t.angle += TURRET_SPIN * dt;
      t.x = this.x + Math.cos(t.angle) * TURRET_ORBIT;
      t.y = this.y + Math.sin(t.angle) * TURRET_ORBIT;

      t.fireTimer -= dt * this.intensity;
      if (t.fireTimer <= 0) {
        t.fireTimer = TURRET_FIRE;
        const a = Math.atan2(py - t.y, px - t.x);
        bullets.spawn({
          x: t.x, y: t.y, dirX: Math.cos(a), dirY: Math.sin(a),
          speed: TURRET_SHOT_SPEED * this.bulletSpeedMul, life: 4, radius: 9, damage: 1,
          friendly: false, texKey: 'bullet/enemy/bolt', rotates: true,
        });
      }
    }
  }

  /** Core sealed for the sweep; rage shielded between breaths (docs/02 B1). */
  private get coreOpen(): boolean {
    if (this.stage === 'core') return !this.laser.busy;
    if (this.stage === 'rage') return this.breath <= 0;
    return true;
  }

  override get isVulnerable(): boolean {
    return super.isVulnerable && this.coreOpen;
  }

  // ---- Collision overrides (armor: turrets only; later: body) ----

  /** Armor stage: aim at the nearest turret, not the sealed hull (X7). */
  override aimPoint(px: number, py: number): { x: number; y: number } {
    if (this.stage !== 'armor') return this;
    return nearestTurret(this.turrets, px, py, Infinity) ?? this;
  }

  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    if (this.stage !== 'armor') return super.absorbBullets(bullets);

    let hits = 0;
    let dealt = 0;
    for (const t of this.turrets) {
      if (!t.alive) continue;
      hits += bullets.collideCircle(true, t.x, t.y, TURRET_RADIUS, (dmg) => {
        const taken = this.hurtTurret(t, dmg);
        dealt += taken;
        return taken; // this turret's own loss
      });
    }
    // Armored body still eats bullets without damage (clink)
    hits += bullets.collideCircle(true, this.x, this.y, this.def.radius, () => undefined, false);
    return { hits, dealt };
  }

  override hitByAoe(x: number, y: number, radius: number): boolean {
    if (this.stage !== 'armor') return super.hitByAoe(x, y, radius);
    return nearestTurret(this.turrets, x, y, radius + TURRET_RADIUS) !== null;
  }

  /** Armor stage: the turret IN the blast takes it (X5); hull clinks. */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    if (this.stage !== 'armor') return super.applyAoe(x, y, radius, damage);
    const t = nearestTurret(this.turrets, x, y, radius + TURRET_RADIUS);
    if (!t) return 0;
    this.markAoe(t.x, t.y, TURRET_RADIUS);
    return this.hurtTurret(t, damage);
  }

  override takeDamage(amount: number): boolean {
    if (this.stage === 'armor') {
      const t = this.turrets.find((u) => u.alive);
      if (t) this.hurtTurret(t, amount);
      return false;
    }
    if (!this.coreOpen) {
      this.hitFlash = 0.08; // sealed hatch / shielded core: clink
      return false;
    }
    const mul = this.stage === 'rage' ? WINDOWS.breathMul : this.vent > 0 ? WINDOWS.ventMul : 1;
    return super.takeDamage(amount * mul);
  }

  /** Turret damage also drains the bar down to the core share. */
  private hurtTurret(t: Turret, amount: number): number {
    if (!t.alive || this.damageLocked) return 0;
    const dealt = Math.min(t.hp, amount);
    t.hp -= amount;
    this.hp = Math.max(this.maxHp * CORE_HP_PCT, this.hp - dealt);
    if (t.hp <= 0) this.killTurret(t);
    return dealt;
  }

  override touchesPlayer(px: number, py: number, playerRadius: number): boolean {
    if (super.touchesPlayer(px, py, playerRadius)) return true;
    if (this.laser.playerInBeam) return true;
    if (this.stage === 'rage') {
      // Outside the containment ring = burning red zone
      if (Math.hypot(px - this.homeX, py - this.homeY) > this.ringRadius) return true;
    }
    return false;
  }

  private killTurret(t: Turret): void {
    if (!t.alive) return;
    t.alive = false;
    t.sprite.visible = false;
    this.events?.burst(t.x, t.y, BOSS_FEEDBACK.wardenPart);
    if (this.turrets.every((u) => !u.alive)) {
      this.hp = this.maxHp * CORE_HP_PCT;
      this.enterStage('core');
    }
  }

  override render(alpha: number): void {
    super.render(alpha);
    const cx = this.prevX + (this.x - this.prevX) * alpha;
    const cy = this.prevY + (this.y - this.prevY) * alpha;

    for (const t of this.turrets) {
      if (!t.alive) continue;
      t.sprite.position.set(t.x - cx, t.y - cy);
      t.sprite.rotation = t.angle + Math.PI; // barrel faces outward-ish
    }

    this.laser.render(this.beamG);
    // The hatch reads the window: sealed frame while closed, glow before a breath
    if (this.stage !== 'armor') {
      this.body.texture = getTexture('boss/warden/body', this.coreOpen ? 1 : 0);
      const warn = this.stage === 'rage' && this.breath > 0 && this.breath < WINDOWS.breathWarn;
      if (warn && Math.sin(this.time * 30) > 0) this.body.tint = WINDOWS.warnTint;
      else if (this.stage === 'core' && this.vent > 0) this.body.tint = WINDOWS.ventTint;
    }

    // Rage containment ring (world-space around home, drawn relative to boss)
    this.ringG.clear();
    if (this.stage === 'rage') {
      const pulse = 0.5 + Math.sin(this.time * 6) * 0.2;
      this.ringG
        .circle(this.homeX - cx, this.homeY - cy, this.ringRadius)
        .stroke({ color: COLORS.dangerRed, width: 10, alpha: pulse });
    }
  }
}
